import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMultiCmt } from '../../contexts/MultiCmtContext';
import { useFirestore } from '../../contexts/FirestoreContext';

import { auth } from '../../config/firebase';
import { doc, getDoc, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { Button, Spin, Alert, Card, Typography, Tag, Space, message, Modal, Timeline, List, Drawer, Tooltip, Popconfirm } from 'antd';
import { SaveOutlined, DownloadOutlined, SecurityScanOutlined, EditOutlined, CheckCircleOutlined, HistoryOutlined, ReloadOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * PlanEditor Component - Secure in-app PDF plan editing with CMT isolation
 * Features: iframe communication, autosave, security validation, error handling
 */
const PlanEditor = ({ planKey, onClose }) => {
  const { activeCmt } = useMultiCmt();
  const { db } = useFirestore();
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [fieldCount, setFieldCount] = useState(0);
  
  // Template loading state
  const [templateUrl, setTemplateUrl] = useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  const [error, setError] = useState(null);
  
  // 🎯 PRODUCTION FEATURES STATE
  // Version history and audit
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [currentETag, setCurrentETag] = useState(null);
  
  // Save and conflict management
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [conflictInfo, setConflictInfo] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Error handling and feedback
  const [errorHistory, setErrorHistory] = useState([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  
  // Auto-save configuration
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  
  // Temporary stubs for other functionality
  const planData = {};
  const planConfig = {};
  const isLoading = false;
  const isSaving = false;
  const hasAccess = true;
  const isInitialized = true;
  const handleFieldChange = useCallback(() => {}, []);
  const getCurrentFields = useCallback(() => ({}), []);
  const forceSave = useCallback(() => Promise.resolve(), []);
  const planTitle = planKey?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Plan';

  // 🔧 Load template URL from Firestore with SURGICAL crisis_plan proxy logic
  useEffect(() => {
    const loadTemplate = async () => {
      if (!planKey || !db) return;
      
      try {
        setIsLoadingTemplate(true);
        console.log('🔍 Loading template for planKey:', planKey);
        
        // 🎯 SURGICAL IMPLEMENTATION: Crisis Plan Proxy Logic
        if (planKey === 'crisis_plan') {
          console.log('🎯 Crisis Plan detected - using proxy endpoint');
          
          // Validate authentication for proxy
          const firebaseUser = auth.currentUser;
          if (!firebaseUser) {
            throw new Error('User not authenticated for crisis plan access');
          }
          
          try {
            // Test authentication by getting ID token
            await firebaseUser.getIdToken();
            
            // Use proxy endpoint for crisis_plan only
            const proxyUrl = `/api/crisis-plan-pdf?planKey=${encodeURIComponent(planKey)}&cmtId=${encodeURIComponent(activeCmt?.id || '')}`;
            console.log('✅ Crisis Plan proxy URL configured:', proxyUrl);
            
            setTemplateUrl(proxyUrl);
            setError(null);
            setIsLoadingTemplate(false);
            return;
            
          } catch (authError) {
            console.error('❌ Authentication error for crisis plan proxy:', authError);
            throw new Error(`Authentication failed: ${authError.message}`);
          }
        }
        
        // 📋 DEFAULT BEHAVIOR: All other digital products use direct Firebase Storage URLs
        console.log('📋 Using direct Firebase Storage for planKey:', planKey);
        
        const productRef = doc(db, 'digital_products', planKey);
        const productDoc = await getDoc(productRef);
        
        if (!productDoc.exists()) {
          throw new Error(`Digital product not found: ${planKey}`);
        }
        
        const productData = productDoc.data();
        console.log('✅ Template loaded:', productData.downloadURL);
        
        setTemplateUrl(productData.downloadURL);
        setError(null);
        setIsLoadingTemplate(false);
        
      } catch (err) {
        console.error('❌ Error loading template:', err);
        setError(`Failed to load template: ${err.message}`);
        setIsLoadingTemplate(false);
      }
    };
    
    loadTemplate();
  }, [planKey, db, activeCmt?.id]);

  // 🔧 Load version history for Crisis Plan - COPIED FROM WORKING CRISIS COMMUNICATION PLAN
  useEffect(() => {
    if (!planKey || !activeCmt?.id || !db) return;

    console.log('🔍 Setting up Crisis Plan version history listener...');
    
    const versionsRef = collection(db, 'crisis_plan_versions');
    const versionsQuery = query(
      versionsRef,
      where('planKey', '==', planKey),
      where('cmtId', '==', activeCmt.id),
      orderBy('version', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(versionsQuery, (snapshot) => {
      const versions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().createdAt?.toDate() || new Date(),
        createdBy: doc.data().createdBy || 'Unknown'
      }));
      
      console.log('📚 Crisis Plan version history updated:', versions.length, 'versions');
      setVersionHistory(versions);
      
      if (versions.length > 0) {
        setCurrentVersion(versions[0].version);
      }
    }, (error) => {
      console.error('❌ Error loading Crisis Plan version history:', error);
    });

    return unsubscribe;
  }, [planKey, activeCmt?.id, db]);
  
  // 🎯 PRODUCTION FEATURES: SAVE ORCHESTRATION
  const triggerSave = useCallback(async () => {
    if (!iframeRef.current?.contentWindow) {
      throw new Error('PDF iframe not ready');
    }
    
    try {
      setSaveStatus('saving');
      console.log('🎯 Triggering PDF save from parent component...');
      
      // Send save request to iframe
      iframeRef.current.contentWindow.postMessage({
        type: 'PDF_SAVE_REQUEST'
      }, window.location.origin);
      
      // Wait for save completion or error
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Save operation timed out'));
        }, 60000); // 60 second timeout
        
        const handleSaveResponse = (event) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'PDF_SAVE_ERROR') {
            clearTimeout(timeout);
            window.removeEventListener('message', handleSaveResponse);
            setSaveStatus('error');
            addError('Save failed', event.data.error);
            reject(new Error(event.data.error));
          } else if (event.data.type === 'PDF_SAVE_SUCCESS') {
            clearTimeout(timeout);
            window.removeEventListener('message', handleSaveResponse);
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            setHasUnsavedChanges(false);
            message.success('Plan saved successfully!');
            resolve(event.data);
          }
        };
        
        window.addEventListener('message', handleSaveResponse);
      });
      
    } catch (error) {
      setSaveStatus('error');
      addError('Save orchestration failed', error);
      throw error;
    }
  }, []);
  
  // 🎯 PRODUCTION FEATURES: ERROR MANAGEMENT
  const addError = useCallback((message, error) => {
    const errorEntry = {
      id: Date.now(),
      message,
      error: error?.message || error,
      timestamp: new Date(),
      stack: error?.stack
    };
    
    setErrorHistory(prev => [errorEntry, ...prev.slice(0, 9)]); // Keep last 10 errors
    console.error(`❌ ${message}:`, error);
  }, []);
  
  // 🎯 PRODUCTION FEATURES: CONFLICT RESOLUTION
  const handleConflict = useCallback((conflictData) => {
    setConflictInfo(conflictData);
    setShowConflictModal(true);
    setSaveStatus('error');
    addError('Save conflict detected', 'Another user has modified this plan');
  }, [addError]);
  
  const resolveConflict = useCallback(async (strategy) => {
    try {
      setShowConflictModal(false);
      setConflictInfo(null);
      
      if (strategy === 'refresh') {
        // Refresh the page to get latest version
        window.location.reload();
      } else if (strategy === 'overwrite') {
        // Force save with current version
        await triggerSave();
      }
    } catch (error) {
      addError('Conflict resolution failed', error);
    }
  }, [triggerSave, addError]);
  
  /**
   * Get current user's ID token for authentication
   */
  const getCurrentIdToken = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('User not authenticated');
    }
    return await firebaseUser.getIdToken();
  }, []);
  
  // 🎯 PRODUCTION FEATURES: VERSION ROLLBACK
  const rollbackToVersion = useCallback(async (version) => {
    try {
      console.log(`🔄 Rolling back to version ${version.version}...`);
      
      // Show progress indicator
      message.loading({ content: `Rolling back to version ${version.version}...`, key: 'rollback' });
      
      // Call the save endpoint to rollback to this version
      const response = await fetch('/api/save-crisis-plan-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getCurrentIdToken()}`
        },
        body: JSON.stringify({
          planKey,
          cmtId: activeCmt?.id,
          rollbackToVersion: version.version,
          metadata: {
            formFieldCount: version.metadata?.formFieldCount || 0,
            lastModified: new Date().toISOString(),
            rollbackFrom: currentVersion?.version || 'latest'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Rollback failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Success - update UI and reload iframe
      message.success({ 
        content: `✅ Successfully rolled back to version ${version.version}`, 
        key: 'rollback' 
      });
      
      // Create ultra-simple version object (React is very picky about objects)
      const cleanVersion = {
        id: `rollback_${Date.now()}`,
        version: typeof result.newVersion === 'number' ? result.newVersion : (currentVersion?.version ? currentVersion.version + 1 : 1),
        createdAt: new Date().toISOString(), // Use string instead of Date object
        createdBy: String(auth.currentUser?.email || 'Current User'),
        changeDescription: `Rolled back to version ${version.version}`,
        isLatest: true,
        planKey: String(planKey || 'crisis_plan'),
        cmtId: String(activeCmt?.id || 'unknown')
      };
      
      console.log('🔧 Skipping state update for now to avoid React error');
      // TODO: Fix React state issue - temporarily disabled
      // try {
      //   setCurrentVersion(cleanVersion);
      //   console.log('✅ State updated successfully');
      // } catch (stateError) {
      //   console.error('❌ State update failed:', stateError);
      //   throw stateError;
      // }
      
      // Force iframe reload to show the rolled-back version
      setTimeout(() => {
        try {
          if (iframeRef.current) {
            const currentSrc = iframeRef.current.src;
            const separator = currentSrc.includes('?') ? '&' : '?';
            const newSrc = `${currentSrc}${separator}v=${Date.now()}&rollback=true`;
            console.log('🔄 Reloading iframe with rolled-back version:', newSrc);
            iframeRef.current.src = newSrc;
          }
        } catch (reloadError) {
          console.error('❌ Iframe reload failed:', reloadError);
        }
      }, 100); // Minimal delay to ensure backend save completed
      
    } catch (error) {
      console.error('❌ Rollback error:', error);
      message.error({ 
        content: `Failed to rollback: ${error.message}`, 
        key: 'rollback' 
      });
      addError('Version rollback failed', error);
    }
  }, [addError, planKey, activeCmt?.id, currentVersion, iframeRef]);

  /**
   * Handle messages from PDF iframe with enhanced security
   */
  const handleIframeMessage = useCallback(async (event) => {
    // Security: Validate origin
    if (event.origin !== window.location.origin) {
      console.warn('Rejected iframe message from unauthorized origin:', event.origin);
      return;
    }

    // 🎯 SURGICAL IMPLEMENTATION: Handle ID token requests for crisis plan proxy
    if (event.data.type === 'REQUEST_ID_TOKEN') {
      console.log('🎯 ID token requested by crisis plan iframe');
      
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          throw new Error('User not authenticated');
        }
        
        const idToken = await firebaseUser.getIdToken();
        
        // Send token back to iframe
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'ID_TOKEN_RESPONSE',
            token: idToken
          }, window.location.origin);
          
          console.log('✅ ID token sent to crisis plan iframe');
        }
        
      } catch (error) {
        console.error('❌ Failed to get ID token for iframe:', error);
        
        // Send error response
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'ID_TOKEN_RESPONSE',
            token: null,
            error: error.message
          }, window.location.origin);
        }
      }
      
      return;
    }

    // Security: Validate source
    if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
      console.warn('Rejected iframe message from unauthorized source');
      return;
    }

    const { type, field, value, planKey: msgPlanKey, cmtId, fieldCount: detectedFields } = event.data;

    // Validate plan context
    if (msgPlanKey !== planKey || cmtId !== activeCmt?.id) {
      console.warn('Message context mismatch:', { msgPlanKey, planKey, cmtId, currentCmtId: activeCmt?.id });
      return;
    }

    switch (type) {
      case 'PDF_READY':
        setPdfReady(true);
        // Send current field values to PDF
        sendMessageToIframe({
          type: 'PDF_APPLY_VALUES',
          data: {
            fields: getCurrentFields()
          }
        });
        break;

      case 'PDF_FIELDS_DETECTED':
        setFieldCount(detectedFields || 0);
        break;

      case 'PDF_FORM_CHANGE':
        if (field && hasAccess) {
                  await handleFieldChange(field, value);
        setLastSaved(new Date());
      }
      break;

    case 'PDF_CURRENT_VALUES':
      // Handle current values response if needed
      break;

    default:
      console.warn('Unknown iframe message type:', type);
  }
}, [planKey, activeCmt?.id, hasAccess, handleFieldChange, getCurrentFields]);

/**
 * Send message to iframe with error handling
 */
const sendMessageToIframe = useCallback((message) => {
  if (iframeRef.current && iframeRef.current.contentWindow && iframeLoaded) {
    try {
      iframeRef.current.contentWindow.postMessage(message, window.location.origin);
    } catch (error) {
      console.error('Failed to send message to iframe:', error);
    }
  }
}, [iframeLoaded]);

/**
 * Handle iframe load event
 */
const handleIframeLoad = useCallback(() => {
  setIframeLoaded(true);
}, []);

/**
 * Handle manual save
 */
const handleManualSave = useCallback(async () => {
  try {
    await forceSave();
    setLastSaved(new Date());
    message.success('Plan saved successfully');
  } catch (error) {
    message.error('Failed to save plan');
  }
}, [forceSave]);

/**
 * Handle PDF export/download
 */
const handleExport = useCallback(async () => {
  try {
    console.log('📥 Initiating PDF download...');
    
    if (!templateUrl) {
      message.error('PDF not ready for download');
      return;
    }
    
    // Download the current PDF (either saved version or original template)
    const downloadUrl = templateUrl;
    
    // Create download link
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${planTitle || 'Crisis Plan'}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('✅ PDF download initiated successfully');
    message.success('PDF download started');
    
  } catch (error) {
    console.error('❌ Error downloading PDF:', error);
    message.error('Failed to download PDF. Please try again.');
  }
}, [templateUrl, planTitle]);

  // Setup message listener
  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  // Build iframe URL with security parameters
  const iframeUrl = templateUrl ? 
    `/plan.html?file=${encodeURIComponent(templateUrl)}&planKey=${encodeURIComponent(planKey)}&cmtId=${encodeURIComponent(activeCmt?.id || '')}` 
    : null;

  // Error state (template loading failed)
  if (error && !isLoadingTemplate) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: 600, textAlign: 'center' }}>
          <SecurityScanOutlined style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }} />
          <Title level={3} type="danger">
            Template not available
          </Title>
          <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '24px' }}>
            {error}
          </Text>
          <Button onClick={onClose}>
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading || isLoadingTemplate) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        textAlign: 'center'
      }}>
        <Spin size="large" style={{ marginBottom: '16px' }} />
        <Title level={4} type="secondary">
          {isLoadingTemplate ? 'Loading template...' : 'Initializing plan editor...'}
        </Title>
        <Text type="secondary">
          Validating CMT access and permissions
        </Text>
      </div>
    );
  }

  // Error state
  if (error && !hasAccess) {
    return (
      <Alert
        message="Unable to load plan editor"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card 
        style={{ 
          marginBottom: '8px', 
          borderRadius: '6px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <EditOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {planTitle}
              </Title>
              <Space style={{ marginTop: '4px' }}>
                <Tag 
                  icon={<SecurityScanOutlined />} 
                  color="blue"
                >
                  CMT: {activeCmt?.name || 'Unknown'}
                </Tag>
                {pdfReady && (
                  <Tag 
                    icon={<CheckCircleOutlined />} 
                    color="green"
                  >
                    {fieldCount} fields detected
                  </Tag>
                )}
              </Space>
            </div>
          </div>
          
          <Space>
            {/* Save status */}
            {isSaving ? (
              <Space>
                <Spin size="small" />
                <Text type="secondary">Saving...</Text>
              </Space>
            ) : lastSaved ? (
              <Text type="secondary">
                Saved {lastSaved.toLocaleTimeString()}
              </Text>
            ) : null}
            
            {/* Action buttons */}
            <Button
              icon={<SaveOutlined />}
              onClick={triggerSave}
              disabled={isSaving}
              size="small"
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: 'white'
              }}
            >
              💾 Save Plan
            </Button>
            
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!pdfReady}
              size="small"
            >
              Export
            </Button>
            
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setShowVersionHistory(true)}
              disabled={!pdfReady}
              size="small"
            >
              Version History
            </Button>
            
            <Button onClick={onClose} size="small">
              Close
            </Button>
          </Space>
        </div>
      </Card>

      {/* PDF Editor Iframe */}
      <div style={{ flex: 1, position: 'relative' }}>
        {iframeUrl ? (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            onLoad={handleIframeLoad}
            sandbox="allow-scripts allow-same-origin"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '4px'
            }}
            title={`${planTitle} Editor`}
          />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <Alert
              message="Template not available"
              description="Please contact your administrator."
              type="warning"
              showIcon
            />
          </div>
        )}
        
        {/* Loading overlay */}
        {!iframeLoaded && iframeUrl && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}>
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" style={{ marginBottom: '16px' }} />
              <Text>Loading PDF editor...</Text>
            </div>
          </div>
        )}
      </div>
      
      {/* 🎯 PRODUCTION FEATURES: VERSION HISTORY DRAWER */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            Version History
            {currentVersion && (
              <Tag color="blue">Current: v{currentVersion}</Tag>
            )}
          </Space>
        }
        placement="right"
        onClose={() => setShowVersionHistory(false)}
        open={showVersionHistory}
        width={400}
      >
        <Timeline>
          {versionHistory.map((version, index) => (
            <Timeline.Item
              key={version.id}
              color={index === 0 ? 'blue' : 'gray'}
              dot={index === 0 ? <CheckCircleOutlined style={{ color: '#1890ff' }} /> : <ClockCircleOutlined />}
            >
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Version {version.version}</Text>
                {index === 0 && <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>Current</Tag>}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                {version.uploadedAt?.toLocaleString()} by {version.createdBy}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                Changes: {version.changesSummary?.changeCount || 0}
              </div>
              {version.fileName && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  File: {version.fileName}
                </div>
              )}
              {index > 0 && (
                <Popconfirm
                  title="Rollback to this version?"
                  description="This will replace the current version. Are you sure?"
                  onConfirm={() => rollbackToVersion(version)}
                  okText="Yes, Rollback"
                  cancelText="Cancel"
                >
                  <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}>
                    <ReloadOutlined /> Rollback to this version
                  </Button>
                </Popconfirm>
              )}
            </Timeline.Item>
          ))}
        </Timeline>
        
        {versionHistory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <HistoryOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <div>No version history available</div>
          </div>
        )}
      </Drawer>
      
      {/* 🎯 PRODUCTION FEATURES: CONFLICT RESOLUTION MODAL */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            Save Conflict Detected
          </Space>
        }
        open={showConflictModal}
        onCancel={() => setShowConflictModal(false)}
        footer={[
          <Button key="refresh" onClick={() => resolveConflict('refresh')}>
            <ReloadOutlined /> Refresh Page
          </Button>,
          <Button key="overwrite" type="primary" danger onClick={() => resolveConflict('overwrite')}>
            <SaveOutlined /> Force Save
          </Button>
        ]}
      >
        <Alert
          type="warning"
          message="Another user has modified this plan while you were editing."
          description={
            <div>
              <p>You have two options:</p>
              <ul>
                <li><strong>Refresh Page:</strong> Load the latest version (you'll lose your changes)</li>
                <li><strong>Force Save:</strong> Overwrite the other user's changes with yours</li>
              </ul>
              <p style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
                Current ETag: {conflictInfo?.currentETag}<br/>
                Your ETag: {conflictInfo?.providedETag}
              </p>
            </div>
          }
          style={{ marginBottom: '16px' }}
        />
      </Modal>
      
      {/* 🎯 PRODUCTION FEATURES: ERROR DETAILS MODAL */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            Error Details
          </Space>
        }
        open={showErrorDetails}
        onCancel={() => setShowErrorDetails(false)}
        footer={[
          <Button key="close" onClick={() => setShowErrorDetails(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <List
          dataSource={errorHistory}
          renderItem={(error, index) => (
            <List.Item key={error.id}>
              <List.Item.Meta
                title={
                  <div style={{ color: '#ff4d4f' }}>
                    {error.message}
                  </div>
                }
                description={
                  <div>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Time:</strong> {error.timestamp.toLocaleString()}
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                      <strong>Error:</strong> {error.error}
                    </div>
                    {error.stack && (
                      <details style={{ marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', color: '#666' }}>
                          Stack trace
                        </summary>
                        <pre style={{ 
                          fontSize: '10px', 
                          backgroundColor: '#f5f5f5', 
                          padding: '8px', 
                          marginTop: '4px',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {error.stack}
                        </pre>
                      </details>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
        
        {errorHistory.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            <CheckCircleOutlined style={{ fontSize: '24px', marginBottom: '8px', color: '#52c41a' }} />
            <div>No errors recorded</div>
          </div>
        )}
      </Modal>
      
      {/* 🎯 PRODUCTION FEATURES: FLOATING ACTION BUTTONS */}
      <div style={{ 
        position: 'fixed', 
        bottom: '20px', 
        right: '20px', 
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>

        
        {/* Error Details Button (only show if there are errors) */}
        {errorHistory.length > 0 && (
          <Tooltip title="View error details">
            <Button
              size="large"
              icon={<ExclamationCircleOutlined />}
              onClick={() => setShowErrorDetails(true)}
              style={{ 
                backgroundColor: '#fff2e8',
                borderColor: '#ffbb96',
                color: '#fa541c'
              }}
            >
              Errors ({errorHistory.length})
            </Button>
          </Tooltip>
        )}
      </div>
      
      {/* 🎯 PRODUCTION FEATURES: STATUS INDICATORS */}
      {(saveStatus !== 'idle' || lastSavedAt) && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          maxWidth: '300px'
        }}>

          
          {saveStatus === 'saved' && (
            <Alert
              type="success"
              message="Changes saved successfully"
              showIcon
              style={{ marginBottom: '8px' }}
            />
          )}
          
          {saveStatus === 'error' && (
            <Alert
              type="error"
              message="Save failed"
              description="Click for details"
              showIcon
              onClick={() => setShowErrorDetails(true)}
              style={{ marginBottom: '8px', cursor: 'pointer' }}
            />
          )}
          
          {lastSavedAt && saveStatus === 'idle' && (
            <div style={{
              fontSize: '12px',
              color: '#666',
              textAlign: 'right',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #d9d9d9'
            }}>
              Last saved: {lastSavedAt.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanEditor;
